import { Api } from '@common/services/api';
import type { User, UserStatus } from '@common/types/objects';

const OPEN_ENDED_SNOOZE_UNTIL = '2099-12-31T23:59:59.000Z';

export function isStatusExpired(status?: UserStatus | null): boolean {
  if (!status?.expires_at) return false;
  return new Date(status.expires_at).getTime() <= Date.now();
}

function getLiveStatus(user?: User | null, liveStatus?: UserStatus | null): UserStatus | null {
  return liveStatus ?? user?.membership?.status ?? user?.status ?? null;
}

/** Banner only when they have an active status AND have paused notifications. */
export function hasPausedNotifications(
  user?: User | null,
  liveStatus?: UserStatus | null,
): boolean {
  const status = getLiveStatus(user, liveStatus);
  if (!status || isStatusExpired(status)) return false;

  const pauseFromStatus = liveStatus?.pause_notifications
    ?? user?.membership?.status?.pause_notifications
    ?? user?.status?.pause_notifications;

  if (pauseFromStatus === true) return true;
  if (pauseFromStatus === false) return false;

  return !!user?.membership?.notification_status?.snooze_enabled;
}

export function getSnoozeUntilForStatus(
  pause: boolean,
  expiresAt: string | null,
): string | null {
  if (!pause) return null;
  return expiresAt || OPEN_ENDED_SNOOZE_UNTIL;
}

export async function syncStatusNotificationSnooze(
  organisationId: string,
  pause: boolean,
  expiresAt: string | null,
): Promise<void> {
  await Api.post(`/v1/organisations/${organisationId}/users/me/notifications/snooze`, {
    snooze_until: getSnoozeUntilForStatus(pause, expiresAt),
  });
}

export async function syncStatusNotificationSnoozeWithRetry(
  organisationId: string,
  pause: boolean,
  expiresAt: string | null,
): Promise<void> {
  try {
    await syncStatusNotificationSnooze(organisationId, pause, expiresAt);
  } catch {
    await syncStatusNotificationSnooze(organisationId, pause, expiresAt);
  }
}
